<template lang="pug">
    extends templates/SelectField.pug
</template>

<script>
import { isUndefined, isEqual, find } from 'underscore'
import { mapGetters, mapState } from 'vuex'
import { dynamicMapState } from '@/common/util/store'
import Events from '@/configuration/Events'

/** MIXINS */
import { SelectFieldMixin } from '@/host/components/mixins/SelectField'
import { InstallmentMixin } from '@/host/components/mixins/Installment'

export default {
  name: 'KryptonInstallments',
  mixins: [SelectFieldMixin, InstallmentMixin],
  inject: ['context'],
  data() {
    return { fieldName: 'installmentNumber' }
  },
  computed: {
    ...mapState(['amount', 'country', 'hasInterests', 'contentLabels']),
    ...mapState({
      dnaAmount: state => state.dna.amount
    }),
    ...mapGetters(['translate', 'getAmountLabel']),
    ...dynamicMapState('context.namespace', ['brandFallbacks']),
    ...dynamicMapState('context.namespace', {
      binCuotas: state => state.binOptions.cuotas
    }),
    isReadOnly() {
      const hasBrandOption = find(this.availableOptions, x => !!x.brand)
      return (
        this.isDisabled ||
        this.isUnitaryTest ||
        this.isDefaultBrand ||
        (this.isDebitBrand && !hasBrandOption) ||
        this.availableOptions.length === 1
      )
    },
    hasInstallments() {
      return (
        this.selectedBrand === 'DEFAULT' && this.configuration?.hidden === false
      )
    },
    selectedInstallment() {
      const option = find(
        this.availableOptions,
        option => option.id === this.selectedOption
      )

      let installmentCount = null
      let hasInterests = null
      let totalAmount = null
      const brand = option?.brand ? option.brand : null
      if (this.selectedOption === '-1' && !this.isDefaultBrand) {
        installmentCount = 1
        hasInterests = false
        totalAmount = this.amount
      } else if (option && option.id !== '-1') {
        installmentCount = option.count
        hasInterests = !!option.hasInterests
        totalAmount = option.totalAmount
      }

      return { installmentCount, totalAmount, hasInterests, brand }
    }
  },
  watch: {
    binCuotas: 'resetData',
    selectedInstallment: {
      deep: true,
      handler(selectedInstallment, oldSelectedInstallment) {
        const installment = { ...selectedInstallment }
        if (selectedInstallment.brand) {
          this.paymentBrand = selectedInstallment.brand
        } else if (
          !this.isDefaultBrand &&
          installment.installmentCount &&
          !(this.selectedBrand in this.brandFallbacks)
        ) {
          this.paymentBrand = this.selectedBrand
          installment.brand = this.selectedBrand
        }

        if (!isEqual(installment, oldSelectedInstallment)) {
          this.$bus.$emit(
            Events.krypton.installments.change,
            this.context.formId,
            installment
          )
        }
      }
    }
  },
  methods: {
    /**
     * Format of the installment property:
     * https://intranet.portal.lyra-network.com/pages/viewpage.action?pageId=11928728
     *
     * format:
     * 0:1:2:3:4:5
     * 0: Number of installments
     * 1: Individual installment currency amount
     * 2: Labels defined by KJS (interest types)
     * 3: Specific label (Overrides all other values if present)
     * 4: Brand (If set by the merchant it becomes the selected Card Brand) sent to pp
     * 5: Total Amount (sum of all the amounts of the options)
     */
    parseValues(value, id) {
      let [
        configCount,
        amount,
        interestType,
        dnaLabel,
        brand,
        configTotalAmount
      ] = value.split(':')

      // For Cuota Simple options (formerly Ahora), use the first value as count (id != count)
      const count = `${dnaLabel}`.match(/(Ahora|Cuota Simple)/)
        ? parseInt(configCount)
        : parseInt(id)

      const hasInterests =
        interestType !== '' ? parseInt(interestType) : undefined

      const totalAmount =
        !configTotalAmount && !hasInterests
          ? this.amount
          : parseInt(configTotalAmount)

      return { count, amount, hasInterests, dnaLabel, brand, totalAmount }
    },
    /**
     * Generate an available option from the given data
     */
    generateOption(value, id) {
      const { count, amount, hasInterests, dnaLabel, brand, totalAmount } =
        this.parseValues(value, id)

      // Get the proper label
      const label = this.generateLabel(
        count,
        amount,
        hasInterests,
        totalAmount,
        dnaLabel
      )

      return { id, count, amount, hasInterests, label, brand, totalAmount }
    },

    /**
     * Generate the label with the given data
     */
    generateLabel(count, amount, hasInterests, totalAmount, dnaLabel) {
      const susForm = count === 1 ? 'singular' : 'plural'

      // Label
      let label
      if (!this.contentLabels.installmentNumber[susForm]) {
        // If label is defined in dna take it
        if (dnaLabel && dnaLabel.length) {
          label = `${dnaLabel}`
          // if plan Cuota Simple (formerly Ahora), calculate label
          if (label?.match(/(Ahora|Cuota Simple)/)) {
            label = 'Cuota Simple [COUNT] de [CURRENCY] [AMOUNT]'
          }
        } else {
          /**
           * Plan Z exception - see KJS-4419
           * We always show the specific label for all the languages
           * No need to translate or replace variables, return the string directly
           */
          if (count === 11 && this.selectedBrand === 'NARANJA') {
            return 'Plan Z'
          }
          if (count === -1) {
            label =
              this.formPlaceholder[
                `singlePayment${!this.isDebitBrand ? 'Credit' : ''}`
              ] ||
              this.translate(
                `installment_single_payment${
                  !this.isDebitBrand ? '_credit' : ''
                }`
              )
          } else {
            // Else get label from translator
            let type = 'simple'
            if (
              amount.length &&
              !isUndefined(hasInterests) &&
              hasInterests <= 1
            ) {
              type = 'with' + (hasInterests === 0 ? '' : 'out') + '_int'
            } else if (hasInterests === 2) {
              type = 'with_mini_cuotas'
            } else if (hasInterests === 3) {
              type = 'with_cuotas'
            } else if (hasInterests === 4) {
              type = 'with_tasa_0'
            }
            label = this.translate(`installments_${type}_${susForm}`)
          }
        }
      } else {
        label = `${this.contentLabels.installmentNumber[susForm]}`
      }

      // Replacements
      label = label.replace('[COUNT]', count)
      if (amount.length) {
        amount = this.getAmountLabel(amount)
        label = label.replace('[CURRENCY] [AMOUNT]', amount)
      }
      if (this.bankName) label += ` - ${this.bankName}`
      if (this.country === 'AR' && totalAmount)
        label += ` (${this.getAmountLabel(totalAmount)})`

      return label
    },

    sortOptions() {
      this.availableOptions.sort((a, b) => a.count - b.count)
    },

    generatePlaceholder() {
      const type = {}
      if (this.isWalletMyCards && !this.paymentMethodToken) {
        type.name = 'wallet'
        type.translation = 'installments_wallet'
      } else if (this.isDefaultBrand) {
        type.name = 'default'
        type.translation = 'placeholder_default_installment_number'
      } else if (this.hasNoOptions) {
        type.name = `singlePayment${!this.isDebitBrand ? 'Credit' : ''}`
        type.translation = `installment_single_payment${
          !this.isDebitBrand ? '_credit' : ''
        }`
      }

      if (this.formPlaceholder[type.name]) {
        return this.formPlaceholder[type.name]
      } else if (type.name) {
        return this.translate(type.translation)
      }
    },

    manageSelectedOption(value) {
      for (const option of this.availableOptions) {
        if (option.id === value) {
          const hasInterests = option.hasInterests === true
          let amount = this.dnaAmount
          // Total amount defined, update the amount label (For all)
          if (option.totalAmount) {
            amount = option.totalAmount
          } else {
            this.$store.dispatch('update', { hasInterests })
          }

          this.$store.dispatch('update', { amount, hasInterests })
          break
        }
      }
    }
  }
}
</script>
